import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Card from '../base/Card';

interface ChartData {
  label: string;
  value: number;
  color: string;
  details?: { [key: string]: number };
}

interface AdvancedChartProps {
  title: string;
  data: ChartData[];
  type: 'income' | 'expense' | 'investment' | 'balance';
  allowLineChart?: boolean;
}

type ChartType = 'pie' | 'bar' | 'line';

export default function AdvancedChart({ title, data, type, allowLineChart = true }: AdvancedChartProps) {
  const navigate = useNavigate();
  const [chartType, setChartType] = useState<ChartType>('pie');
  const [selectedItem, setSelectedItem] = useState<ChartData | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  const handleItemClick = (item: ChartData | { payload?: ChartData }) => {
    const chartData = 'payload' in item && item.payload ? item.payload : item as ChartData;
    
    // Navegar a la página correspondiente con filtro de categoría
    if (type === 'expense') {
      navigate(`/dashboard/egresos?categoria=${encodeURIComponent(chartData.label)}`);
    } else if (type === 'income') {
      navigate(`/dashboard/ingresos?categoria=${encodeURIComponent(chartData.label)}`);
    } else if (type === 'investment') {
      navigate(`/dashboard/inversiones?tipo=${encodeURIComponent(chartData.label)}`);
    }
  };
  
  const closeDetails = () => {
    setShowDetails(false);
    setSelectedItem(null);
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Transformar datos para Recharts
  const rechartData = data.map((item, index) => ({
    name: item.label,
    value: item.value,
    color: item.color,
    details: item.details,
    index
  }));

  // Componente de tooltip personalizado
  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: { name: string; fullLabel?: string }; value: number }> }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const displayName = data.payload.fullLabel || data.payload.name;
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-800">{displayName}</p>
          <p className={`font-bold ${type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatCurrency(data.value)}
          </p>
          {total > 0 && chartType !== 'line' && (
            <p className="text-sm text-gray-500">
              {((data.value / total) * 100).toFixed(1)}% del total
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Generar datos del mes actual (día a día hasta la fecha actual)
  const generateLineData = () => {
    const hoy = new Date();
    const currentYear = hoy.getFullYear();
    const currentDay = hoy.getDate();
    const nombreMes = hoy.toLocaleDateString('es-ES', { month: 'long' });
    
    if (type === 'investment') {
      // Para inversiones, generar datos evolutivos acumulativos por tipo
      return generateInvestmentEvolutionData();
    }
    
    const dias = [];
    const valorDiarioPromedio = total / currentDay; // Distribución promedio por día transcurrido
    
    // Generar datos para cada día desde el 1 hasta el día actual
    for (let dia = 1; dia <= currentDay; dia++) {
      const variacion = 0.7 + Math.random() * 0.6; // Variación entre 70% y 130% del promedio
      const valorDia = Math.floor(valorDiarioPromedio * variacion);
      
      dias.push({
        name: dia.toString(),
        value: valorDia,
        fullLabel: `${dia} de ${nombreMes} ${currentYear}`
      });
    }
    
    return dias;
  };

  // Generar datos evolutivos para inversiones (acumulativo por tipo)
  const generateInvestmentEvolutionData = () => {
    const hoy = new Date();
    const currentYear = hoy.getFullYear();
    const currentDay = hoy.getDate();
    const nombreMes = hoy.toLocaleDateString('es-ES', { month: 'long' });
    
    const dias = [];
    
    // Para cada día del mes, calcular el acumulado de cada tipo de inversión
    for (let dia = 1; dia <= currentDay; dia++) {
      const diaData: Record<string, string | number> = {
        name: dia.toString(),
        fullLabel: `${dia} de ${nombreMes} ${currentYear}`
      };
      
      // Agregar cada tipo de inversión como una serie separada
      data.forEach(item => {
        // Simular crecimiento acumulativo para cada tipo
        const porcentajeDia = dia / currentDay;
        const valorBase = item.value * porcentajeDia;
        const variacion = 0.8 + Math.random() * 0.4; // Variación entre 80% y 120%
        diaData[item.label] = Math.floor(valorBase * variacion);
      });
      
      dias.push(diaData);
    }
    
    return dias;
  };

  const lineData = generateLineData();

  if (data.length === 0) {
    return (
      <Card className="h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-2">📊</div>
          <p>No hay datos disponibles</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="h-full relative">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h3 className="text-xl font-bold text-gray-800">{title}</h3>
          <div className="flex bg-gray-100 rounded-lg p-1 relative z-0">
            <button
              onClick={() => setChartType('pie')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap relative ${
                chartType === 'pie' 
                  ? 'bg-white text-emerald-600 shadow-sm' 
                  : 'text-gray-600 hover:text-emerald-600'
              }`}
            >
              <i className="ri-pie-chart-line mr-1"></i>
              Torta
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap relative ${
                chartType === 'bar' 
                  ? 'bg-white text-emerald-600 shadow-sm' 
                  : 'text-gray-600 hover:text-emerald-600'
              }`}
            >
              <i className="ri-bar-chart-line mr-1"></i>
              Barras
            </button>
            {allowLineChart && (
              <button
                onClick={() => setChartType('line')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap relative ${
                  chartType === 'line' 
                    ? 'bg-white text-emerald-600 shadow-sm' 
                    : 'text-gray-600 hover:text-emerald-600'
                }`}
              >
                <i className="ri-line-chart-line mr-1"></i>
                Líneas
              </button>
            )}
          </div>
        </div>
        
        <div className="min-h-[300px]">
          {chartType === 'pie' && (
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <ResponsiveContainer width={280} height={280}>
                  <PieChart>
                    <Pie
                      data={rechartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="#fff"
                      strokeWidth={2}
                      onClick={handleItemClick}
                    >
                      {rechartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color}
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="flex-1 space-y-3">
                {data.map((item, index) => {
                  const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0';
                  return (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleItemClick(item)}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <div>
                          <div className="font-medium text-gray-800">{item.label}</div>
                          <div className="text-sm text-gray-600">{percentage}% del total</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-900">
                          {formatCurrency(item.value)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {chartType === 'bar' && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={rechartData} 
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#666' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#666' }}
                  tickFormatter={(value) => {
                    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
                    return `$${value}`;
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="value" 
                  radius={[4, 4, 0, 0]}
                  stroke="#fff"
                  strokeWidth={1}
                  onClick={handleItemClick}
                  className="cursor-pointer"
                >
                  {rechartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}

          {chartType === 'line' && (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart 
                data={lineData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#666' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#666' }}
                  tickFormatter={(value) => {
                    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
                    return `$${value}`;
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                
                {type === 'investment' ? (
                  // Múltiples líneas para inversiones
                  data.map((item) => (
                    <Line 
                      key={item.label}
                      type="monotone" 
                      dataKey={item.label}
                      stroke={item.color}
                      strokeWidth={2}
                      dot={{ fill: item.color, strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: item.color, strokeWidth: 2 }}
                    />
                  ))
                ) : (
                  // Línea única para otros tipos
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={type === 'income' ? '#10b981' : '#ef4444'}
                    strokeWidth={3}
                    dot={{ fill: type === 'income' ? '#10b981' : '#ef4444', strokeWidth: 2, r: 6 }}
                    activeDot={{ r: 8, stroke: type === 'income' ? '#10b981' : '#ef4444', strokeWidth: 2 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-gray-700">Total:</span>
            <span className={`text-xl font-bold ${
              type === 'income' ? 'text-emerald-600' : 
              type === 'expense' ? 'text-red-600' : 
              total >= 0 ? 'text-emerald-600' : 'text-red-600'
            }`}>
              {formatCurrency(total)}
            </span>
          </div>
          {chartType === 'line' && (
            <div className="mt-3">
              <div className="text-sm text-gray-500 mb-2">
                * Gráfico de líneas muestra evolución {type === 'investment' ? 'acumulativa por tipo' : 'diaria del mes actual'}
              </div>
              {type === 'investment' && (
                <div className="flex flex-wrap gap-4">
                  {data.map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-0.5 rounded" 
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-xs text-gray-600">{item.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
      
      {/* Modal de detalles */}
      {showDetails && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl font-bold text-gray-800">
                Detalles: {selectedItem.label}
              </h4>
              <button
                onClick={closeDetails}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="font-medium">Total:</span>
                <span className="font-bold text-emerald-600">
                  {formatCurrency(selectedItem.value)}
                </span>
              </div>
              
              {selectedItem.details && Object.entries(selectedItem.details).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center">
                  <span className="text-gray-600">{key}:</span>
                  <span className="font-medium">{formatCurrency(value)}</span>
                </div>
              ))}
            </div>
            
            <button
              onClick={closeDetails}
              className="w-full mt-6 bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </>
  );
}